import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Typography,
  Button,
  Input,
  Card,
  Tag,
  Space,
  Empty,
  Badge,
  Breadcrumb,
  Form,
  message,
  Divider,
} from 'antd';
import {
  MessageOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Doubt, DoubtCategory } from '../types';
import { analyzeMeetingDoubts } from '../services/api.service';

const { Content } = Layout;
const { Text } = Typography;
const { TextArea } = Input;

const CATEGORY_COLORS: Record<DoubtCategory, string> = {
  price: 'red',
  timing: 'orange',
  taxes: 'gold',
  energy: 'green',
  sustainability: 'cyan',
  logistics: 'blue',
  quality: 'purple',
  others: 'default',
};

const CATEGORY_LABELS: Record<DoubtCategory, string> = {
  price: 'Precio',
  timing: 'Plazos',
  taxes: 'Impuestos',
  energy: 'Energía',
  sustainability: 'Sostenibilidad',
  logistics: 'Logística',
  quality: 'Calidad',
  others: 'Otros',
};

const TYPE_LABELS: Record<'doubt' | 'concern', string> = {
  doubt: 'Duda',
  concern: 'Preocupación',
};

const TYPE_COLORS: Record<'doubt' | 'concern', string> = {
  doubt: 'blue',
  concern: 'volcano',
};

const TRANSCRIPT_PLACEHOLDER = `Ejemplo:
Cliente: ¿Cuánto tiempo tardaría la instalación?
Comercial: Aproximadamente 3 semanas desde la firma del contrato.
Cliente: ¿Y qué pasa si hay retrasos? ¿Quién asume los costes extra?
Cliente: También me preocupa el tema de los impuestos, ¿el precio ya los incluye?`;

const MeetingDoubtsPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<{ title: string; transcript: string }>();
  const [loading, setLoading] = useState(false);
  const [doubts, setDoubts] = useState<Doubt[] | null>(null);

  const handleAnalyze = async (values: { title: string; transcript: string }) => {
    if (!values.transcript?.trim()) {
      message.warning('Pega la transcripción de la reunión antes de analizar.');
      return;
    }

    setLoading(true);
    setDoubts(null);

    try {
      const result = await analyzeMeetingDoubts(values.transcript.trim(), values.title);
      setDoubts(result.doubts);
      if (result.doubts.length === 0) {
        message.info('No se encontraron dudas en esta transcripción.');
      } else {
        message.success(`${result.doubts.length} duda${result.doubts.length !== 1 ? 's' : ''} encontrada${result.doubts.length !== 1 ? 's' : ''}.`);
      }
    } catch (err) {
      message.error('Error al analizar la reunión. Comprueba que el executor está disponible.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: 'transparent' }}>

      <Content style={{ padding: '32px', maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        {/* Breadcrumb */}
        <Breadcrumb
          style={{ marginBottom: 24 }}
          items={[
            {
              title: (
                <span
                  style={{ cursor: 'pointer', color: '#1677ff' }}
                  onClick={() => navigate('/marketplace')}
                >
                  Marketplace
                </span>
              ),
            },
            { title: 'Analizador de Dudas' },
          ]}
        />

        {/* Input form */}
        <Card
          bordered={false}
          style={{ borderRadius: 12, marginBottom: 24 }}
          title={
            <Space>
              <MessageOutlined style={{ color: '#1677ff' }} />
              <span>Transcripción de la Reunión</span>
            </Space>
          }
        >
          <Form form={form} layout="vertical" onFinish={handleAnalyze}>
            <Form.Item name="title" label="Título de la reunión (opcional)">
              <Input
                placeholder="Ej: Presentación Presupuesto - Cliente XYZ"
                style={{ maxWidth: 480 }}
              />
            </Form.Item>

            <Form.Item
              name="transcript"
              label="Transcripción"
              rules={[{ required: true, message: 'La transcripción es obligatoria.' }]}
            >
              <TextArea
                autoSize={{ minRows: 8, maxRows: 20 }}
                placeholder={TRANSCRIPT_PLACEHOLDER}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<ThunderboltOutlined />}
                loading={loading}
                size="large"
              >
                Analizar Reunión
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* Results */}
        {doubts !== null && (
          <Card
            bordered={false}
            style={{ borderRadius: 12 }}
            title={
              <Space>
                <span>Resultados</span>
                <Badge count={doubts.length} color="#1677ff" />
              </Space>
            }
          >
            {doubts.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No se encontraron dudas ni preocupaciones en esta transcripción."
                style={{ padding: '40px 0' }}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {doubts.map((doubt, idx) => (
                  <Card
                    key={idx}
                    size="small"
                    bordered={false}
                    style={{ background: '#fafafa', borderRadius: 8 }}
                  >
                    {/* Header row: tags */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <Tag color={CATEGORY_COLORS[doubt.category]}>
                        {CATEGORY_LABELS[doubt.category]}
                      </Tag>
                      <Tag color={TYPE_COLORS[doubt.type]}>
                        {TYPE_LABELS[doubt.type]}
                      </Tag>
                    </div>

                    {/* Spanish description (primary) */}
                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>
                      {doubt.descripcion}
                    </Text>

                    {/* English description (secondary) */}
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                      {doubt.description}
                    </Text>

                    {/* Tags */}
                    {doubt.tags.length > 0 && (
                      <>
                        <Divider style={{ margin: '8px 0' }} />
                        <Space size={4} wrap>
                          {doubt.tags.map((tag) => (
                            <Tag key={tag} style={{ fontSize: 12 }}>
                              {tag}
                            </Tag>
                          ))}
                        </Space>
                      </>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </Card>
        )}
      </Content>
    </Layout>
  );
};

export default MeetingDoubtsPage;
